import { Component, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewChecked, HostListener, Inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { AIService, DocumentAnalysis, DocumentCharacteristics } from '../services/ai.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ModuleTrackerService } from '../services/module-tracker.service';
import { NavigationHistoryService } from '../services/navigation-history.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import { ChatHistoryService, ChatMessage } from '../services/chat-history.service';
import { v4 as uuidv4 } from 'uuid';

interface UploadedDocument {
  file: File;
  content: string;
  characteristics: DocumentCharacteristics | null;
  selected: boolean;
  focusRange?: { start: number, end: number }; // Page or paragraph range
  tags: string[];
  dateAdded: Date;
}

// Add a type for local message selection
interface LocalChatMessage extends ChatMessage {
  selected?: boolean;
}

// Document processing states
type DocumentProcessingState = 'idle' | 'uploading' | 'analyzing' | 'ready' | 'error';

// Academic content types
type ContentType = 'lecture-notes' | 'slides' | 'exercises' | 'quiz' | 'exam' | 'summary';

interface ExportOptions {
  fileType: 'docx' | 'pptx' | 'txt';
  fileName: string;
}

// Dialog component for document analysis guide
@Component({
  selector: 'app-guide-dialog',
  template: `
    <h2 mat-dialog-title>{{data.title}}
      <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </h2>
    <mat-dialog-content class="guide-content">
      <div [innerHTML]="data.content | safe: 'html'"></div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #1a3c5a;
      margin: 0;
      padding: 16px 24px;
      font-size: 20px;
      background-color: #f5f7fa;
      border-bottom: 1px solid #e0e0e0;
    }
    .close-button {
      margin-right: -12px;
    }
    .guide-content {
      max-height: 70vh;
      padding: 20px 24px;
      overflow-y: auto;
      line-height: 1.6;
    }
    ::ng-deep .guide-content h1 {
      font-size: 1.8em;
      color: #1a3c5a;
      margin-top: 0.5em;
      margin-bottom: 0.5em;
      border-bottom: 1px solid #eaecef;
      padding-bottom: 0.3em;
    }
    ::ng-deep .guide-content h2 {
      font-size: 1.5em;
      color: #1a3c5a;
      margin-top: 1em;
      margin-bottom: 0.5em;
      border-bottom: 1px solid #eaecef;
      padding-bottom: 0.3em;
      background: none;
    }
    ::ng-deep .guide-content h3 {
      font-size: 1.3em;
      color: #1a3c5a;
      margin-top: 1em;
      margin-bottom: 0.5em;
    }
    ::ng-deep .guide-content h4 {
      font-size: 1.1em;
      color: #1a3c5a;
      margin-top: 1em;
      margin-bottom: 0.5em;
    }
    ::ng-deep .guide-content li {
      margin-bottom: 8px;
    }
    ::ng-deep .guide-content strong {
      color: #1a3c5a;
    }
    ::ng-deep .guide-content a {
      color: #1976d2;
      text-decoration: none;
    }
    ::ng-deep .guide-content a:hover {
      text-decoration: underline;
    }
    mat-dialog-actions {
      padding: 12px 24px;
      border-top: 1px solid #e0e0e0;
    }
  `]
})
export class GuideDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<GuideDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {title: string, content: string}
  ) {}
}

/**
 * Dialog to select document chunk
 */
@Component({
  selector: 'chunk-selection-dialog',
  template: `
    <h2 mat-dialog-title>Analyze Document Section</h2>
    <mat-dialog-content>
      <p>
        Your document <strong>{{data.fileName}}</strong> is quite large 
        (approximately {{data.estimatedPages}} pages).
      </p>
      <p>
        For better performance, select a specific section to analyze:
      </p>
      <div class="page-range-inputs">
        <mat-form-field appearance="outline">
          <mat-label>Start Page</mat-label>
          <input matInput type="number" [(ngModel)]="startPage" min="1" [max]="data.estimatedPages">
        </mat-form-field>
        
        <span class="range-separator">to</span>
        
        <mat-form-field appearance="outline">
          <mat-label>End Page</mat-label>
          <input matInput type="number" [(ngModel)]="endPage" [min]="startPage" [max]="data.estimatedPages">
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="null">Cancel</button>
      <button mat-raised-button color="primary" 
              [mat-dialog-close]="{startPage: startPage, endPage: endPage}"
              [disabled]="!startPage || !endPage || startPage > endPage">
        Analyze This Section
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .page-range-inputs {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 20px;
    }
    .range-separator {
      margin: 0 10px;
    }
  `]
})
export class ChunkSelectionDialogComponent {
  startPage: number = 1;
  endPage: number = 5;
  
  constructor(
    public dialogRef: MatDialogRef<ChunkSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Initialize with first 5 pages or less if document is smaller
    this.endPage = Math.min(5, data.estimatedPages);
  }
}

@Component({
  selector: 'app-text-module',
  templateUrl: './text-module.component.html',
  styleUrls: ['./text-module.component.scss']
})
export class TextModuleComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  
  activeInterface: 'direct' | 'document' | 'academic' = 'direct';
  textForm: FormGroup;
  academicForm: FormGroup;
  documentForm: FormGroup;
  
  // Multi-document support
  uploadedDocuments: UploadedDocument[] = [];
  selectedDocumentIndices: number[] = [];
  
  selectedDocument: File | null = null;
  documentContent: string | null = null;
  isGenerating = false;
  isDragging = false;
  directChatHistory: LocalChatMessage[] = [];
  documentChatHistory: LocalChatMessage[] = [];
  academicChatHistory: LocalChatMessage[] = [];
  selectedResponses: string[] = [];
  
  // Properties for improved UX
  documentProcessingState: DocumentProcessingState = 'idle';
  processingProgress = 0;
  documentCharacteristics: DocumentCharacteristics | null = null;
  processingError: string | null = null;
  
  // Progress indicators
  processingStep: string = '';
  progressBarMode: 'determinate' | 'indeterminate' = 'determinate';
  
  // Academic content options
  contentTypes: {value: ContentType, label: string}[] = [
    {value: 'lecture-notes', label: 'Lecture Notes'},
    {value: 'slides', label: 'Presentation Slides'},
    {value: 'quiz', label: 'Quiz Questions'},
    {value: 'exam', label: 'Exam Questions'},
    {value: 'summary', label: 'Topic Summary'}
  ];
  
  academicLevels: {value: string, label: string}[] = [
    {value: 'undergraduate', label: 'Undergraduate'},
    {value: 'graduate', label: 'Graduate'},
    {value: 'advanced', label: 'Advanced/Research'},
    {value: 'general', label: 'General Public'}
  ];
  
  // Document tags for organization
  availableTags: string[] = [
    'Course Materials', 
    'Research', 
    'Lecture Notes', 
    'Readings', 
    'References',
    'Drafts'
  ];
  
  // Scrolling control
  private shouldScrollToBottom = false;
  private isUserScrolling = false;
  private scrollTimeout: any;
  private lastScrollHeight = 0;
  private lastScrollTop = 0;
  
  // Subscriptions
  private subscriptions: Subscription[] = [];
  
  showHelp = true;
  showContentGuide = true;
  focusAreasControl = new FormControl([]);
  selectedFocusAreas: string[] = [];
  
  // Add a new FormControl for the chat input
  chatInputControl = new FormControl('');
  documentChatInputControl = new FormControl('');
  academicChatInputControl = new FormControl('');
  
  showExportButton = false;
  
  // Message editing state
  editingMessageId: string | null = null;
  editMessageContent: string = '';
  
  // For suggestion handling
  private _currentSuggestion: { original: string, enhanced: string, actionType?: string, actionId?: string } | null = null;
  
  // Theme handling
  isDarkMode = false;
  activeSectionId: string | null = null;
  
  constructor(
    private fb: FormBuilder,
    private aiService: AIService,
    private snackBar: MatSnackBar,
    private moduleTracker: ModuleTrackerService,
    private router: Router,
    private dialog: MatDialog,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private chatHistoryService: ChatHistoryService,
    private changeDetectorRef: ChangeDetectorRef,
    private navigationHistory: NavigationHistoryService
  ) {
    this.textForm = this.fb.group({
      prompt: ['']
    });
    
    this.documentForm = this.fb.group({
      question: [''],
      documentFocus: ['all'],
      customRange: [''],
      selectedTags: [[]]
    });
    
    this.academicForm = this.fb.group({
      topic: [''],
      contentType: ['lecture-notes'],
      academicLevel: ['undergraduate'],
      contentLength: [5],
      additionalInstructions: ['']
    });

    // Subscribe to focus areas changes
    this.focusAreasControl.valueChanges.subscribe(values => {
      this.selectedFocusAreas = values;
    });
    
    // Redirect to direct query if academic is accessed while hidden
    if (this.activeInterface === 'academic') {
      this.activeInterface = 'direct';
    }
  }

  ngOnInit(): void {
    // Initialize dark mode from localStorage
    const savedDarkMode = localStorage.getItem('ai-assistant-dark-mode');
    if (savedDarkMode === 'true') {
      this.isDarkMode = true;
      // Apply theme classes for dark mode
      document.documentElement.classList.add('theme-dark');
      document.body.classList.add('theme-alternate');
    }
    
    // Set the default active section
    setTimeout(() => {
      this.setActiveSection('input-section');
    }, 100);
    
    // Retrieve chat history from service, but only if it hasn't been cleared
    if (!this.chatHistoryService.isDirectHistoryCleared()) {
      this.directChatHistory = this.chatHistoryService.getDirectChatHistory();
    } else {
      this.directChatHistory = [];
    }
    
    if (!this.chatHistoryService.isDocumentHistoryCleared()) {
      this.documentChatHistory = this.chatHistoryService.getDocumentChatHistory();
    } else {
      this.documentChatHistory = [];
    }
    
    // Set the active module to 'text'
    this.moduleTracker.setActiveModule('text');
    
    // Track the current interface in navigation history
    this.navigationHistory.trackInterfaceChange('text', this.activeInterface);
    
    // Subscribe to focus areas changes
    this.focusAreasControl.valueChanges.subscribe(values => {
      this.selectedFocusAreas = values;
    });
    
    // Subscribe to encoder loading status
    this.subscriptions.push(
      this.aiService.encoderLoading$.subscribe(isLoading => {
        if (isLoading) {
          this.snackBar.open('Loading semantic search capabilities...', '', {
            duration: 3000
          });
        }
      })
    );
    
    // Subscribe to chat history from ChatHistoryService
    this.subscriptions.push(
      this.chatHistoryService.directChatHistory$.subscribe(history => {
        // Only update if history hasn't been cleared
        if (!this.chatHistoryService.isDirectHistoryCleared()) {
          this.directChatHistory = history as LocalChatMessage[];
        }
      })
    );
    
    this.subscriptions.push(
      this.chatHistoryService.documentChatHistory$.subscribe(history => {
        // Only update if history hasn't been cleared
        if (!this.chatHistoryService.isDocumentHistoryCleared()) {
          this.documentChatHistory = history as LocalChatMessage[];
        }
      })
    );
  }
  
  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && !this.isUserScrolling) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
    
    // Detect changes manually after view checked to prevent ExpressionChangedAfterChecked error
    this.changeDetectorRef.detectChanges();
  }
  
  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Scroll to the bottom of the chat container
  private scrollToBottom(): void {
    // Don't scroll if user is interacting with content
    if (this.isUserScrolling) {
      return;
    }
    
    try {
      if (this.chatContainer && this.chatContainer.nativeElement) {
        const element = this.chatContainer.nativeElement;
        
        // Use setTimeout to ensure the DOM has been updated
        setTimeout(() => {
          // Double-check that the user isn't interacting before scrolling
          if (!this.isUserScrolling) {
            element.scrollTop = element.scrollHeight;
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  // Track user scrolling
  @HostListener('scroll', ['$event'])
  onScroll(event: Event): void {
    // Set user scrolling flag when user interacts with the scrollbar
    this.isUserScrolling = true;
      
    if (this.chatContainer && this.chatContainer.nativeElement) {
      const element = this.chatContainer.nativeElement;
      
      // Check if user has scrolled to bottom
      const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;
      
      // If scrolled to bottom, allow auto-scrolling again
      if (isAtBottom) {
        this.isUserScrolling = false;
      }
      
      // Clear and set a new timeout to reset the user scrolling flag
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      this.scrollTimeout = setTimeout(() => {
        if (isAtBottom) {
          this.isUserScrolling = false;
        }
      }, 1000);
    }
  }

  // Add event listener for mouseover to prevent auto-scrolling when hovering over content
  @HostListener('mouseover', ['$event'])
  onMouseOver(event: MouseEvent): void {
    // If user is hovering over content, especially in academic section, disable auto-scrolling
    const target = event.target as HTMLElement;
    if (target && (
        target.closest('.academic-content') || 
        target.closest('.formatted-content') || 
        target.closest('.message-content') ||
        target.closest('.chat-message'))) {
      
      // Prevent auto-scrolling
      this.isUserScrolling = true;
      
      // Clear any existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // Set a longer timeout to maintain prevention of auto-scrolling
      this.scrollTimeout = setTimeout(() => {
        // Check if user is still interacting before re-enabling auto-scroll
        if (this.chatContainer && this.chatContainer.nativeElement) {
          const element = this.chatContainer.nativeElement;
          const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;
          
          if (isAtBottom) {
            this.isUserScrolling = false;
          }
        }
      }, 10000); // 10 seconds
    }
  }

  // Add event listener for mousedown to prevent auto-scrolling when selecting text
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    // If user is clicking on any text content, disable auto-scrolling
    if (event.target && 
        (event.target as HTMLElement).closest('.message-content, .chat-message, pre, code')) {
      // Prevent auto-scrolling for a longer period when interacting with content
      this.isUserScrolling = true;
      
      // Clear any existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // Set longer timeout (15 seconds) to allow user to copy content
      this.scrollTimeout = setTimeout(() => {
        // After timeout, check if we should re-enable auto-scrolling
        if (this.chatContainer && this.chatContainer.nativeElement) {
          const element = this.chatContainer.nativeElement;
          const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;
          
          if (isAtBottom) {
            this.isUserScrolling = false;
          }
        }
      }, 15000); // 15 seconds
    }
  }
  
  // Override the copy behavior to prevent scroll to bottom
  copyToClipboard(content: string): void {
    // Prevent scrolling to bottom when copying
    this.isUserScrolling = true;
    
    try {
      // Create a temporary DOM element to analyze the content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Check if the content contains tables
      const hasTables = tempDiv.querySelector('table') !== null;
      
      if (hasTables && navigator.clipboard && navigator.clipboard.write) {
        // Enhanced copying for tables - preserve formatting
        this.copyWithFormatting(content, tempDiv);
      } else {
        // For non-table content, use the plain text approach
        const plainTextContent = this.convertHtmlToPlainText(content);
        
        // Try using the modern Clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(plainTextContent)
            .then(() => {
              this.snackBar.open('Content copied to clipboard as plain text', 'Close', {
                duration: 3000,
                panelClass: ['modern-snackbar', 'success-snackbar']
              });
            })
            .catch(err => {
              console.error('Clipboard API failed:', err);
              // Fall back to execCommand method
              this.fallbackCopyToClipboard(plainTextContent);
            });
        } else {
          // Fall back to execCommand method for older browsers
          this.fallbackCopyToClipboard(plainTextContent);
        }
      }
    } catch (err) {
      console.error('Failed to copy content:', err);
      this.snackBar.open('Failed to copy content', 'Close', {
        duration: 3000,
        panelClass: ['modern-snackbar', 'error-snackbar']
      });
    }
    
    // Block auto-scrolling for a longer time after copying in academic section
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    // Always keep user scrolling flag true for a while after copying
    this.scrollTimeout = setTimeout(() => {
      // Only reset if user is at the bottom
      if (this.chatContainer && this.chatContainer.nativeElement) {
        const element = this.chatContainer.nativeElement;
        const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;
        
        if (isAtBottom) {
          this.isUserScrolling = false;
        }
      }
    }, this.activeInterface === 'academic' ? 20000 : 5000); // 20 seconds for academic, 5 for others
  }

  // Enhanced method to copy content with HTML formatting preserved
  private copyWithFormatting(content: string, tempElement: HTMLElement): void {
    try {
      // Clean up the tables - preserve only essential styling
      const tables = tempElement.querySelectorAll('table');
      tables.forEach(table => {
        // Add CSS class for base styling
        table.classList.add('clipboard-table');
        
        // Add inline styles that work well across applications (Word, Google Docs, etc.)
        table.setAttribute('style', 'border-collapse: collapse; width: auto; margin: 0; border: 1px solid #ccc;');
        table.setAttribute('border', '1');
        table.setAttribute('cellpadding', '5');
        table.setAttribute('cellspacing', '0');
        
        // Style the cells
        const cells = table.querySelectorAll('th, td');
        cells.forEach(cell => {
          // Add basic styling to make the table look good when pasted
          cell.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top;');
        });
        
        // Style the header row if present
        const headerRow = table.querySelector('thead tr');
        if (headerRow) {
          const headerCells = headerRow.querySelectorAll('th');
          headerCells.forEach(cell => {
            cell.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; background-color: #f2f2f2; font-weight: bold; text-align: left;');
          });
        }
        
        // For better compatibility with Microsoft Word and other editors
        // Style alternating rows for better readability
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach((row, index) => {
          if (index % 2 === 1) {
            row.setAttribute('style', 'background-color: #f9f9f9;');
            
            // Also apply styling to all cells in this row
            const rowCells = row.querySelectorAll('td');
            rowCells.forEach(cell => {
              cell.setAttribute('style', 'border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; background-color: #f9f9f9;');
            });
          }
        });
      });
      
      // Create a full HTML document for better compatibility
      // This helps with Word, Outlook and other rich text editors
      const htmlContent = `
        <html>
        <head>
          <style>
            .clipboard-table {
              border-collapse: collapse;
              width: auto;
              border: 1px solid #ccc;
              margin: 0;
            }
            .clipboard-table th, .clipboard-table td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
              vertical-align: top;
            }
            .clipboard-table th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .clipboard-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
          </style>
        </head>
        <body>
          ${tempElement.innerHTML}
        </body>
        </html>
      `;
      
      // First try the modern clipboard API
      if (navigator.clipboard && navigator.clipboard.write) {
        // Create the HTML blob
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        
        // The plain text fallback
        const plainText = tempElement.textContent || '';
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        
        // Create a clipboard item with both formats
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        
        // Write to clipboard
        navigator.clipboard.write([clipboardItem])
          .then(() => {
            this.snackBar.open('Table copied with formatting preserved', 'Close', {
              duration: 3000,
              panelClass: ['modern-snackbar', 'success-snackbar']
            });
          })
          .catch(err => {
            console.error('Enhanced clipboard API failed:', err);
            // Fall back to execCommand for HTML
            this.fallbackCopyHtmlToClipboard(htmlContent);
          });
      } else {
        // Fall back to execCommand for HTML
        this.fallbackCopyHtmlToClipboard(htmlContent);
      }
    } catch (err) {
      console.error('Error in copyWithFormatting:', err);
      // Fall back to plain text copy
      const plainText = this.convertHtmlToPlainText(content);
      this.fallbackCopyToClipboard(plainText);
    }
  }
  
  // Fallback method for copying HTML content in older browsers
  private fallbackCopyHtmlToClipboard(htmlContent: string): void {
    try {
      // Create a temporary container for the HTML content
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);
      
      // Set up selection
      const range = document.createRange();
      range.selectNodeContents(container);
      
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Execute copy command
        const successful = document.execCommand('copy');
        
        if (successful) {
          this.snackBar.open('Table copied with formatting preserved', 'Close', {
            duration: 3000,
            panelClass: ['modern-snackbar', 'success-snackbar']
          });
        } else {
          // If HTML copy fails, try plain text
          const plainText = container.textContent || '';
          this.fallbackCopyToClipboard(plainText);
        }
      } else {
        // If selection is null, fall back to plain text
        const plainText = container.textContent || '';
        this.fallbackCopyToClipboard(plainText);
      }
      
      // Clean up
      document.body.removeChild(container);
      
      // Clear selection if any
      if (selection) {
        selection.removeAllRanges();
      }
    } catch (err) {
      console.error('HTML fallback copy failed:', err);
      // Extract plain text and use the text fallback
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.textContent || '';
      this.fallbackCopyToClipboard(plainText);
    }
  }
  
  // Helper method to convert HTML to plain text
  private convertHtmlToPlainText(html: string): string {
    // Create a temporary DOM element to parse the HTML
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    
    // Replace <br> tags with newline placeholders before getting text content
    const brs = tempElement.querySelectorAll('br');
    brs.forEach(br => {
      br.replaceWith('\n');
    });
    
    // Special handling for the specific format shown in the user's example:
    // Pattern: "<number>. <span class="bullet-point">•</span> <content>"
    const contentNodes = tempElement.childNodes;
    contentNodes.forEach(node => {
      if (node.textContent) {
        // Find the combined numbered list with bullet points pattern (e.g., "1. • Content")
        const match = node.textContent.match(/^(\d+)\.\s*•\s*(.*)/);
        if (match) {
          node.textContent = `${match[1]}. ${match[2]}`;
        }
      }
    });
    
    // Handle bullet points specifically (replace with proper text bullets)
    const bulletPoints = tempElement.querySelectorAll('.bullet-point');
    bulletPoints.forEach(bulletPoint => {
      bulletPoint.textContent = '• ';
    });

    // Handle numbered lists by preserving the numbers
    const listItems = tempElement.querySelectorAll('li');
    let currentListNumber = 0;
    listItems.forEach(li => {
      // If this is an ordered list item, preserve the number
      const parentList = li.parentElement;
      if (parentList && parentList.tagName === 'OL') {
        currentListNumber++;
        li.textContent = `${currentListNumber}. ${li.textContent}`;
      } else {
        // For unordered lists, add a bullet if not already present
        if (!li.textContent.trim().startsWith('•')) {
          li.textContent = `• ${li.textContent}`;
        }
      }
    });
    
    // Process headers to stand out in plain text
    const headers = tempElement.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headers.forEach(header => {
      // Just keep the text, but ensure it gets proper spacing in the output
      header.textContent = `${header.textContent}\n`;
    });
    
    // Handle tables specially to preserve their structure
    const tables = tempElement.querySelectorAll('table');
    tables.forEach(table => {
      const plainTable = this.convertTableToPlainText(table);
      // Replace the table with its plain text representation
      const div = document.createElement('div');
      div.textContent = `\n${plainTable}\n`;
      table.replaceWith(div);
    });
    
    // Get plain text (this includes text content of all elements)
    let plainText = tempElement.textContent || tempElement.innerText || "";
    
    // Fix double spacing and preserve meaningful line breaks
    plainText = plainText.replace(/\n\s+/g, '\n');
    plainText = plainText.replace(/\s{2,}/g, ' ');
    
    // Ensure proper spacing after periods and commas
    plainText = plainText.replace(/(\.|,)(?=\S)/g, '$1 ');
    
    // Ensure blank lines for paragraph breaks
    plainText = plainText.replace(/\n{2,}/g, '\n\n');
    
    // Clean up the specific pattern in the user's example using regex for any that were missed
    plainText = plainText.replace(/(\d+)\.\s*•\s*/g, '$1. ');
    
    // Handle numbered points that may have lost their formatting (like "1.")
    plainText = plainText.replace(/(\d+)\.(?=\S)/g, '$1. ');
    
    return plainText.trim();
  }
  
  // Convert a table element to plain text while preserving structure
  private convertTableToPlainText(table: HTMLTableElement): string {
    const rows = Array.from(table.rows);
    
    // Calculate column widths for better alignment
    const columnWidths: number[] = [];
    rows.forEach(row => {
      const cells = Array.from(row.cells);
      cells.forEach((cell, index) => {
        const cellText = cell.textContent?.trim() || '';
        if (!columnWidths[index] || cellText.length > columnWidths[index]) {
          columnWidths[index] = cellText.length;
        }
      });
    });
    
    // Generate plain text rows with padding for alignment
    const plainTextRows = rows.map(row => {
      const cells = Array.from(row.cells);
      const cellTexts = cells.map((cell, index) => {
        const text = cell.textContent?.trim() || '';
        // Pad with spaces for better alignment (optional)
        // return text.padEnd(columnWidths[index], ' ');
        return text;
      });
      return cellTexts.join('\t');
    });
    
    return plainTextRows.join('\n');
  }
  
  // Fallback copy method for older browsers
  private fallbackCopyToClipboard(content: string): void {
    // Create a temporary textarea element to copy the content
    const textarea = document.createElement('textarea');
    // Preserve formatting as much as possible
    textarea.style.whiteSpace = 'pre-wrap';
    textarea.value = content;
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      this.snackBar.open('Content copied to clipboard as plain text', 'Close', {
        duration: 3000,
        panelClass: ['modern-snackbar', 'success-snackbar']
      });
    } catch (err) {
      console.error('execCommand copy failed:', err);
      this.snackBar.open('Failed to copy content', 'Close', {
        duration: 3000,
        panelClass: ['modern-snackbar', 'error-snackbar']
      });
    }
    
    document.body.removeChild(textarea);
  }

  switchInterface(type: 'direct' | 'document' | 'academic'): void {
    if (this.activeInterface === type) {
      return; // No change needed if staying on same interface
    }
    
    // Temporarily redirect academic to direct query while it's hidden
    if (type === 'academic') {
      type = 'direct';
      this.snackBar.open('Academic content generator is temporarily hidden', 'OK', {
        duration: 3000
      });
    }
    
    console.log(`Switching from ${this.activeInterface} to ${type}`);
    
    // Clear state from the interface we're leaving
    if (this.activeInterface === 'document') {
      // Clear document state when leaving document section
      this.selectedDocument = null;
      this.documentContent = null;
      this.uploadedDocuments = [];
      this.selectedDocumentIndices = [];
      this.documentProcessingState = 'idle';
      this.documentCharacteristics = null;
      
      // Reset the file input element if it exists
      if (this.fileInput && this.fileInput.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    } else if (this.activeInterface === 'academic') {
      // Clear academic state when leaving academic section
      this.focusAreasControl.setValue([]);
      this.selectedFocusAreas = [];
    } else if (this.activeInterface === 'direct') {
      // State is managed by ChatHistoryService, no need to clear it here
    }
    
    // Reset forms based on the new interface
    if (type === 'direct') {
      // Reset direct query form
      this.textForm.reset({
        prompt: ''
      });
    } else if (type === 'document') {
      // Reset document analysis form
      this.documentForm.reset({
        question: '',
        documentFocus: 'all',
        customRange: '',
        selectedTags: []
      });
    } else if (type === 'academic') {
      // Reset academic content form
      this.academicForm.reset({
        topic: '',
        contentType: 'lecture-notes',
        academicLevel: 'undergraduate',
        contentLength: 5,
        additionalInstructions: ''
      });
    }
    
    // Reset common state that should be cleared on interface change
    this.isGenerating = false;
    this.processingProgress = 0;
    this.processingStep = '';
    this.isDragging = false; // Reset drag state
    this.processingError = null; // Clear any error messages
    
    // Set active interface
    this.activeInterface = type;
    
    // Set flag to scroll to bottom after view is updated
    this.shouldScrollToBottom = true;
    
    // Show notification that the section has been refreshed
    setTimeout(() => {
      this.snackBar.open(`Switched to ${this.getInterfaceLabel(type)} - Session preserved`, 'OK', {
        duration: 3000,
        panelClass: ['modern-snackbar', 'info-snackbar']
      });
    }, 100);
    
    // Add to navigation history
    this.navigationHistory.trackInterfaceChange('text', type);
  }

  // Helper method to get user-friendly interface labels
  private getInterfaceLabel(type: 'direct' | 'document' | 'academic'): string {
    switch (type) {
      case 'direct': return 'General Query';
      case 'document': return 'Document Analysis';
      case 'academic': return 'Academic Content';
      default: return type;
    }
  }

  async generateContent(useStoredSuggestion: boolean = false): Promise<void> {
    // Prevent submission if already generating content
    if (this.isGenerating) {
      return;
    }
    
    // Check if focus areas are valid before proceeding (for academic interface)
    if (this.activeInterface === 'academic' && !this.isFocusAreaCountValid()) {
      return;
    }
    
    // Check if this is the first query and needs UI transition (document interface only)
    const isFirstDocumentQuery = this.activeInterface === 'document' && 
        this.documentChatHistory.length === 0 && 
        (useStoredSuggestion || this.documentForm.get('question')?.value);
    
    // Handle content generation based on the active interface
    try {
      this.isGenerating = true;
      this.processingProgress = 0;
      this.progressBarMode = 'determinate';
      
      // Set generic processing step message
      this.processingStep = 'Working on your query...';
      
      // Clear any previous error message
      this.processingError = null;
    
      // Start progress animation
      const progressInterval = setInterval(() => {
        if (this.processingProgress < 80) {
          this.processingProgress += Math.random() * 3 + 1;
        }
      }, 300);
    
      if (this.activeInterface === 'direct') {
        await this.handleDirectQuery();
      } else if (this.activeInterface === 'document') {
        if (useStoredSuggestion && this._currentSuggestion) {
          await this.handleDocumentQuery(this._currentSuggestion.enhanced, this._currentSuggestion.original);
          // Clear the stored suggestion after use
          this._currentSuggestion = null;
        } else {
          await this.handleDocumentQuery();
        }
        
        // Handle UI transition for document interface on first query
        if (isFirstDocumentQuery) {
          setTimeout(() => {
            this.setActiveSection('output-section');
            // Focus on the right input field
            const rightInputField = document.querySelector('.chat-input textarea') as HTMLTextAreaElement;
            if (rightInputField) {
              rightInputField.focus();
            }
          }, 500);
        }
      } else if (this.activeInterface === 'academic') {
        await this.handleAcademicContent();
      }
      
      // Success notification
      this.snackBar.open('Content generated successfully!', 'Dismiss', {
        duration: 3000,
        panelClass: ['modern-snackbar', 'success-snackbar']
      });
      
      // Always clear the interval
      clearInterval(progressInterval);
      
      // Reset progress and hide loader
      this.processingProgress = 100;
      
      // Final cleanup after a short delay
      setTimeout(() => {
        this.isGenerating = false;
    
        // Ensure content is visible by forcing a scroll
        this.isUserScrolling = false;
        this.shouldScrollToBottom = true;
        this.scrollToBottom();
      }, 600);
      
    } catch (error) {
      console.error('Error generating content:', error);
      this.isGenerating = false;
      this.processingProgress = 0;
      
      this.snackBar.open('Error: ' + (error.message || 'An error occurred'), 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar', 'modern-snackbar']
      });
    }
  }

  private async handleDirectQuery(): Promise<void> {
    const prompt = this.textForm.get('prompt')?.value;
    if (!prompt) return;

    // Add user message to chat history via service
    this.chatHistoryService.addDirectChatMessage({
      role: 'user',
      content: prompt,
      timestamp: new Date(),
      id: this.generateMessageId() // Use the private method
    });
    
    // Set flag to scroll to bottom after view is updated
    this.shouldScrollToBottom = true;

    try {
      // Update progress
      this.processingProgress = 30;
      // Keep the generic processing message
      
      // Extract format instructions for tables if present
      const formatInstructions = this.extractTableFormatInstructions(prompt);
      
      // Prepare the enhanced prompt
      let enhancedPrompt = prompt;
      if (formatInstructions) {
        enhancedPrompt += "\n\n" + formatInstructions;
      }
      
      // Send the enhanced prompt to the AI service's direct query method
      const response = await this.aiService.generateTextForDirectQuery(enhancedPrompt).toPromise();
      
      // Format the response
      const formattedResponse = this.formatResponseForDisplay(response);
      
      this.processingProgress = 90;
      
      // Add AI response to chat history via service
      this.chatHistoryService.addDirectChatMessage({
        role: 'assistant',
        content: formattedResponse,
        timestamp: new Date(),
        id: this.generateMessageId(),
        type: 'text'
      });
      
      // Set flag to scroll to bottom after view is updated
      this.shouldScrollToBottom = true;

      // Clear the prompt field
      this.textForm.get('prompt')?.setValue('');
      this.chatInputControl.setValue('');
      
    } catch (error: any) {
      console.error('Error generating text:', error);
      
      // Format error message for display
      let errorMessage = "I'm sorry, I encountered an error";
      
      if (error.message) {
        // Don't show raw API errors to users
        if (error.message.includes('API key') || error.message.includes('Invalid API key')) {
          errorMessage += ": There's an authentication issue with the AI service. Please contact support.";
        } else if (error.message.includes('All available models failed')) {
          errorMessage += ": All available AI models are currently unavailable. Please try again later.";
        } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          errorMessage += ": There appears to be a network connectivity issue. Please check your internet connection and try again.";
        } else if (error.message.includes('timeout') || error.message.includes('Timeout')) {
          errorMessage += ": The request timed out. Please try again with a shorter message or later when the service is less busy.";
        } else {
          // Generic error for unexpected cases
          errorMessage += ": The AI service is currently unavailable. Please try again later.";
        }
      } else {
        errorMessage += ". Please try again.";
      }
      
      // Log the detailed error for debugging
      console.error('Detailed error:', error);
      
      // Add error message to chat history
      this.chatHistoryService.addDirectChatMessage({
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        id: this.generateMessageId(),
        type: 'text'
      });
      
      // Set flag to scroll to bottom after view is updated
      this.shouldScrollToBottom = true;
    } finally {
      // Reset the processing state
      this.processingProgress = 0;
      this.processingStep = '';
      this.isGenerating = false;
    }
  }

  // Add a method to cancel the current operation
  cancelOperation(): void {
    // Reset the generation state
    this.isGenerating = false;
    this.processingProgress = 0;
    
    // For document processing, set state to error
    if (this.activeInterface === 'document' && 
        (this.documentProcessingState === 'uploading' || this.documentProcessingState === 'analyzing')) {
      this.documentProcessingState = 'error';
      this.processingError = 'Document processing was cancelled by user.';
    }
    
    this.snackBar.open('Operation cancelled', 'Close', {
      duration: 3000
    });
  }

  async handleDocumentQuery(
    directQuestion?: string,
    displayQuestion?: string,
    isEditedMessage: boolean = false
  ): Promise<void> {
    // Set a timeout to prevent hanging during document analysis
    const analysisTimeout = setTimeout(() => {
      if (this.isGenerating) {
        this.isGenerating = false;
        this.documentProcessingState = 'error';
        this.processingError = 'Analysis timed out. Please try again with a smaller document or a more specific question.';
        this.snackBar.open('Analysis timed out', 'Dismiss', { duration: 5000 });
      }
    }, 180000); // 3 minute timeout

    try {
      // Use the direct question if provided, otherwise use the form value
      let question = directQuestion || this.documentForm.get('question')?.value || '';
      
      // Store the display question (for chat history display)
      const displayContent = displayQuestion || question;
      
      // Initialize action type and ID as undefined
      let actionType: string | undefined = undefined;
      let actionId: string | undefined = undefined;
      
      // Check if this is a quick action
      if (directQuestion && directQuestion.includes('[QUICK_ACTION:')) {
        // Extract action type and ID from the direct question
        const match = directQuestion.match(/\[QUICK_ACTION:([A-Z_]+):([a-z0-9]+)\]/);
        if (match && match[1] && match[2]) {
          actionType = match[1];
          actionId = match[2];
          
          // Remove the quick action prefix from the question
          question = question.replace(/\[QUICK_ACTION:[A-Z_]+:[a-z0-9]+\]\s*/, '');
        }
      }
      
      // Set processing state - use generic messages that don't include the query
      this.documentProcessingState = 'analyzing';
      this.processingStep = 'Processing your request';
      this.isGenerating = true;
      
      // Add specific instructions based on action type to ensure unique, high-quality outputs
      if (actionType) {
        // Add unique ID to ensure response is distinct from previous ones
        question = `[Request ID: ${actionId}] ${question}`;
        
        switch (actionType) {
          case 'KEY_POINTS':
            question = `${question} Format your response as a focused list of 5-7 key bullet points that capture the most essential information from the document. Start with a brief title, then list points using bullet symbols (•). Each point should be 1-2 sentences, clear and standalone. Focus ONLY on the most important concepts, findings, or arguments from the document. Avoid minor details or examples.`;
            break;
          case 'SUMMARY':
            question = `${question} Create a concise executive summary of the document in paragraph form (max 150 words). Start with a clear title, followed by "Executive Summary:" and then a single well-structured paragraph that captures all essential information. Focus on the main purpose, methodology, findings, and implications while omitting minor details.`;
            break;
          case 'PRESENTATION':
            question = `${question} Create exactly 5 slide concepts for a presentation based on this document. Format as: Slide 1: [Title] followed by 3-4 bullet points of content for each slide. Begin with a title slide, then organize remaining slides in a logical flow. Each slide should have a clear purpose and focus on a key aspect of the document. Keep bullet points brief and impactful.`;
            break;
          case 'OUTLINE':
            question = `${question} Create a structured outline with exactly 5-7 main sections (I, II, III) and relevant subsections (A, B, C) to organize the document's key information. Use proper outline formatting and hierarchy. Focus on creating a logical, well-organized structure that captures the document's main topics and subtopics without excessive detail.`;
            break;
        }
        
        // Add confidence instruction to all quick actions
        question += " Be assertive and confident in your response. Provide a completely fresh perspective with each request, ensuring no duplication with previous responses. Format the output for easy reading and copying.";
      }

      if (!this.uploadedDocuments || this.uploadedDocuments.length === 0) {
        throw new Error('No documents uploaded');
      }

      // Get document content from the first uploaded document
      const documentContent = this.uploadedDocuments[0].content;

      // Set the document context and pass the chat history to maintain conversational context
      this.processingStep = 'Generating response';
      this.processingProgress = 30;

      // Get previous messages for context
      const previousMessages = this.documentChatHistory.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Check if we need to add a new user message to the history
      // Only add if it's not an edited message - if it's edited, we've already updated it
      if (displayContent && !isEditedMessage) {
        // Check if the latest message in history already has this question content
        const lastMessage = this.documentChatHistory.length > 0 ? 
          this.documentChatHistory[this.documentChatHistory.length - 1] : null;
        
        const isAlreadyAdded = lastMessage && 
          lastMessage.role === 'user' && 
          lastMessage.content === displayContent;
        
        // Only add if this exact message isn't already the latest one
        if (!isAlreadyAdded) {
          // For quick actions, mark the message with action type metadata
          const messageContent = actionType ? 
            `${displayContent} [Action: ${actionType}]` : 
            displayContent;
          
          this.chatHistoryService.addDocumentChatMessage({
            id: this.generateMessageId(),
            role: 'user',
            content: messageContent,
            timestamp: new Date()
          });
          
          // Update local history to stay in sync
          this.documentChatHistory = this.chatHistoryService.getDocumentChatHistory();
        }
      }

      // Generate the AI response
      this.processingProgress = 50;
      
      // For edited messages, we want to use the updated question content,
      // but we don't need to add it to history again
      const response = await this.aiService.askQuestionAboutDocument(
        documentContent, 
        question, 
        previousMessages
      ).toPromise();

      if (!response) {
        throw new Error('Failed to generate response');
      }

      // Process the response and add to history
      this.processingStep = 'Finalizing response';
      this.processingProgress = 80;

      // Add the message to chat history
      this.chatHistoryService.addDocumentChatMessage({
        id: this.generateMessageId(),
        role: 'assistant',
        content: this.formatResponseForDisplay(response.content),
        timestamp: new Date(),
        sourceDocuments: this.getSelectedDocumentNames(),
        actionType: actionType // Store action type for reference
      });
      
      // Update local history
      this.documentChatHistory = this.chatHistoryService.getDocumentChatHistory();

      // Reset form state
      this.documentForm.get('question')?.setValue('');
      this._currentSuggestion = null;
      this.processingProgress = 100;
      this.documentProcessingState = 'ready';
      
      // Scroll to new content
      this.shouldScrollToBottom = true;
      clearTimeout(analysisTimeout);
    } catch (error) {
      console.error('Document analysis error:', error);
      this.documentProcessingState = 'error';
      this.processingError = error.message || 'Failed to analyze document';
      this.snackBar.open(this.processingError, 'Dismiss', { duration: 5000 });
      clearTimeout(analysisTimeout);
    } finally {
      this.isGenerating = false;
    }
  }

  /**
   * Modified to accept a direct message parameter and remove predefined formatting
   */
  private async handleAcademicContent(directPrompt?: string): Promise<void> {
    try {
      // Update processing state
      this.processingProgress = 20;
      this.processingStep = 'Preparing academic content request...';
      this.isGenerating = true;
      
      // Construct the prompt without enforcing specific formatting
      let academicPrompt: string;
      
      if (directPrompt) {
        // For direct prompts, use the message as is
        academicPrompt = directPrompt;
      } else {
        // For form-based prompts, build a descriptive prompt from form values
    const formValues = this.academicForm.value;
        academicPrompt = this.buildAcademicPrompt(formValues);
      }
      
      console.log('Academic content prompt:', academicPrompt);
      
      // Update processing state
      this.processingProgress = 40;
      this.processingStep = 'Generating academic content...';
      
      // Create enhanced messages array for academic content
      const enhancedMessages = [];
      
      // Add initial instruction as a user message
      enhancedMessages.push({
        role: 'user',
        content: 'You are an advanced academic content creator specializing in creating educational materials for students at various academic levels. Please respond to the academic request that follows.'
      });
      
      // Add assistant acknowledgment
      enhancedMessages.push({
        role: 'assistant',
        content: 'I understand. I\'ll create high-quality academic content according to your specifications.'
      });
      
      // Add the specific academic content request
      enhancedMessages.push({
        role: 'user',
        content: academicPrompt
      });
      
      // Send request to AI service
      const response = await this.aiService.generateText(enhancedMessages).toPromise();
      
      // Update processing state
      this.processingProgress = 70;
      this.processingStep = 'Finalizing academic content...';
      
      // Apply proper formatting using AI service method
      const enhancedContent = this.aiService.getFormattedContent(response);
      
      // Determine content type for display purposes only
      const contentType = directPrompt ? 'custom' : this.academicForm.get('contentType')?.value;
      
      // Update processing state
                this.processingProgress = 90;
      this.processingStep = 'Finalizing academic content...';
                
      // Add to academic chat history
                this.academicChatHistory.push({
        role: 'assistant',
        content: enhancedContent,
        type: contentType,
        timestamp: new Date(),
        id: this.generateMessageId() // Add ID
      });
      
      // Scroll to bottom
      this.shouldScrollToBottom = true;
      this.scrollToBottom();
      
      // Complete processing
      this.processingProgress = 100;
      
    } catch (error: any) {
      // Handle error
      console.error('Error generating academic content:', error);
      this.handleAcademicContentError(error, directPrompt || this.academicForm.value);
    } finally {
      // Reset processing state
      this.isGenerating = false;
    }
  }
  
  /**
   * Build a prompt for academic content from form values
   * This replaces the previous structured prompt with a more natural language request
   */
  private buildAcademicPrompt(formValues: any): string {
    let prompt = `Please create ${formValues.contentType.replace('-', ' ')} about "${formValues.topic}" `;
    
    // Add academic level if specified
    if (formValues.academicLevel) {
      prompt += `for ${this.getContentTypeLabel(formValues.academicLevel)} level students. `;
    }
    
    // Add content length information
    const unit = this.getContentUnit(formValues.contentType);
    prompt += `It should be approximately ${formValues.contentLength} ${unit} in length. `;
    
    // Add focus areas if specified
    if (this.selectedFocusAreas && this.selectedFocusAreas.length > 0) {
      prompt += `Please focus specifically on the following aspects: ${this.selectedFocusAreas.join(', ')}. `;
    }
    
    // Add general quality instructions
    prompt += "The content should be well-structured, accurate, and educational. Please format the response appropriately with headings, lists, and paragraphs as needed to make the content clear and easy to understand.";
    
    return prompt;
  }

  /**
   * Simplified formatResponse that no longer forces specific structures
   * Just passes through the content with minimal processing
   */
  private formatResponse(content: string, contentType: string): string {
    // No more specific formatting templates - just sanitize and clean
    return content;
  }

  // Remove or simplify the specialized format methods since we're letting the AI structure the response
  // You can either delete these methods or keep simplified versions
  
  // Helper method to handle errors in academic content generation
  private handleAcademicContentError(error: any, formValues: any): void {
    this.academicChatHistory.push({
        role: 'assistant',
      content: `I apologize, but I encountered an error while generating content about "${formValues.topic}". Please try again with more specific requirements or a different topic.`,
        timestamp: new Date(),
        id: this.generateMessageId()
      });
      
      this.shouldScrollToBottom = true;
    this.snackBar.open('Error generating academic content', 'Dismiss', {
      duration: 5000,
      panelClass: ['error-snackbar', 'modern-snackbar']
    });
  }
  
  private extractDocumentRange(content: string, range: { start: number, end: number }): string {
    // Split content into paragraphs
    const paragraphs = content.split(/\n\s*\n/);
    
    // If range is within bounds, extract only those paragraphs
    if (range.start >= 0 && range.end < paragraphs.length && range.start <= range.end) {
      return paragraphs.slice(range.start, range.end + 1).join('\n\n');
    }
    
    return content; // Fall back to full content if range is invalid
  }

  onDocumentSelection(event: any): void {
    // Since we only support one document, always select it
    this.selectedDocumentIndices = [0];
    
    // Update the selected state
    if (this.uploadedDocuments.length > 0) {
      this.uploadedDocuments[0].selected = true;
    }
  }
  
  setDocumentFocusRange(docIndex: number, range: { start: number, end: number }): void {
    if (docIndex >= 0 && docIndex < this.uploadedDocuments.length) {
      this.uploadedDocuments[docIndex].focusRange = range;
      
      // Switch to 'selection' focus mode
      this.documentForm.get('documentFocus')?.setValue('selection');
    }
  }
  
  getSelectedDocumentNames(): string[] {
    return this.selectedDocumentIndices.map(index => this.uploadedDocuments[index].file.name);
  }
  
  getContentTypeLabel(value: string): string {
    // First check if it's an academic level
    const matchingLevel = this.academicLevels.find(level => level.value === value);
    if (matchingLevel) {
      return matchingLevel.label;
    }
    
    // Otherwise check if it's a content type
    const matchingContentType = this.contentTypes.find(type => type.value === value);
    if (matchingContentType) {
      return matchingContentType.label;
    }
    
    return value || 'Unknown';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    
    console.log("Files selected:", input.files.length);
    
    try {
      // Check if there's already a document - only one document allowed
      if (this.uploadedDocuments.length > 0) {
        this.snackBar.open('Only one document is allowed. Please delete the existing document first.', 'Close', {
          duration: 5000
        });
        // Reset the file input
        input.value = '';
        return;
      }
      
      // Get only the first file regardless of how many were selected
      const file = input.files[0];
      
      // Check file size limit (5MB)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeInBytes) {
        this.snackBar.open(`File is too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Maximum size is 5MB.`, 'Close', {
          duration: 5000,
          panelClass: 'error-snackbar'
        });
        // Reset the file input
        input.value = '';
        return;
      }

      // Clear document chat history to prevent context from previous documents influencing analysis
      this.documentChatHistory = [];
      this.chatHistoryService.clearDocumentChatHistory();
      this.snackBar.open('Document chat history cleared for new document analysis', 'OK', {
        duration: 3000
      });
      
      // Process only the first file
      this.documentProcessingState = 'uploading';
      
      this.processUploadedDocument(file).then(() => {
        // File processed
        this.documentProcessingState = 'ready';
        
        // Select the document
        this.updateDocumentSelection();
        
        // Show success message
        this.snackBar.open(`Successfully uploaded document: ${file.name}`, 'Close', {
          duration: 3000
        });
      }).catch(error => {
        console.error("Error processing document:", error);
        this.snackBar.open(`Error uploading document: ${error.message}`, 'Close', {
          duration: 5000,
          panelClass: 'error-snackbar'
        });
        this.documentProcessingState = 'error';
      });
      
      // Reset the file input
      input.value = '';
    } catch (error: any) {
      this.processingError = error.message;
      this.documentProcessingState = 'error';
      // Reset the file input
      input.value = '';
    }
  }
  
  private updateDocumentSelection(): void {
    // Since there's only one document, always select it
    if (this.uploadedDocuments && this.uploadedDocuments.length > 0) {
      this.selectedDocumentIndices = [0];
      this.uploadedDocuments[0].selected = true;
    }
  }
  
  private async processUploadedDocument(file: File): Promise<void> {
    // Update processing state
    this.documentProcessingState = 'uploading';
    this.processingProgress = 10;
    this.processingStep = 'Reading document...';
    
    // Set a timeout to catch long-running processing
    const processingTimeout = setTimeout(() => {
      if (this.documentProcessingState === 'analyzing' || this.documentProcessingState === 'uploading') {
        this.documentProcessingState = 'error';
        this.processingError = 'Document processing timed out. The file may be too large or complex to process.';
        this.snackBar.open('Document processing timed out. Please try with a smaller document or try again later.', 'Close', {
          duration: 5000,
          panelClass: 'error-snackbar'
        });
      }
    }, 180000); // 3 minute timeout (increased from 2 minutes)
    
    try {
      this.processingProgress = 20;
        this.processingStep = 'Extracting text...';
      this.documentProcessingState = 'analyzing';
        
      // Extract text from document
      const content = await this.aiService.analyzeDocument(file);
        
      this.processingProgress = 40;
        this.processingStep = 'Analyzing document characteristics...';
        
        // Detect document characteristics
      const characteristics = this.aiService.detectDocumentCharacteristics(content, file);
      
      // Check if OCR was used (this will be added to the characteristics by the AI service)
      const usedOCR = content.includes('--- Page 1 ---') && content.includes('OCR');
      if (usedOCR) {
        characteristics.extractionMethod = 'OCR';
        characteristics.isScannedDocument = true;
        
        // Show OCR notification to user
        this.snackBar.open('Scanned document detected: OCR technology was used to extract text', 'OK', {
          duration: 6000
        });
      }
      
      // Check if tables were extracted
      const containsTables = content.includes('EXTRACTED TABLES:');
      if (containsTables) {
        characteristics.containsTables = true;
        
        // Show tables notification to user
        this.snackBar.open('Tables detected and extracted from document', 'OK', {
          duration: 4000
        });
      }

      // Enhanced approach: Store the document with chunks for better context handling
      this.processingProgress = 60;
      this.processingStep = 'Processing document for advanced analysis...';
      
      // Get estimated page count for user information
      const estimatedPages = Math.ceil(content.length / 3000); // Approximate chars per page
      
      // Process the document using our improved chunking approach
      // This will split the document into overlapping chunks for better context preservation
      const chunkCount = this.aiService.storeDocumentWithChunks(content);
      
      this.processingProgress = 80;
      this.processingStep = `Finalizing document setup (${chunkCount} chunks)...`;
      
      // Add to uploaded documents array - store full content for reference
      this.uploadedDocuments.push({
        file,
        content: content, // Store full content for reference
        characteristics,
        selected: true,
        tags: [],
        dateAdded: new Date()
      });
      
        this.processingProgress = 100;
      
      // Add processing features information to notification
      let processingDetails = `approximately ${estimatedPages} pages, ${chunkCount} analysis chunks`;
      if (usedOCR) processingDetails += ', OCR processed';
      if (containsTables) processingDetails += ', tables extracted';
      
      // Show success notification with information about document size
      this.snackBar.open(
        `Document "${file.name}" uploaded successfully (${processingDetails})`,
        'Close',
        { duration: 5000 }
      );
      
      // No longer show dialog for manual chunk selection - our automatic chunking handles it
      
      // Clear the timeout since processing completed successfully
      clearTimeout(processingTimeout);
      
      } catch (error: any) {
      // Clear the timeout since we're handling the error
      clearTimeout(processingTimeout);
      
      this.snackBar.open(`Error processing "${file.name}": ${error.message}`, 'Close', {
          duration: 5000
        });
      throw error;
    }
  }
  
  removeDocument(index: number): void {
    if (index >= 0 && index < this.uploadedDocuments.length) {
      // Remove from uploaded documents
      this.uploadedDocuments.splice(index, 1);
      
      // Update selected indices
      this.selectedDocumentIndices = this.selectedDocumentIndices
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      
      // Reset document context in AI service
      this.aiService.resetDocumentContext();
      
      // Clear document chat history
      this.documentChatHistory = [];
      this.chatHistoryService.clearDocumentChatHistory();
      
      // Show notification
      this.snackBar.open('Document removed and chat history cleared', 'Close', {
        duration: 3000
      });
    }
  }
  
  toggleDocumentTag(docIndex: number, tag: string): void {
    if (docIndex >= 0 && docIndex < this.uploadedDocuments.length) {
      const doc = this.uploadedDocuments[docIndex];
      
      if (doc.tags.includes(tag)) {
        // Remove tag
        doc.tags = doc.tags.filter(t => t !== tag);
      } else {
        // Add tag
        doc.tags.push(tag);
      }
    }
  }
  
  filterDocumentsByTags(tags: string[]): number[] {
    if (!tags || tags.length === 0) {
      // Return all document indices if no tags selected
      return this.uploadedDocuments.map((_, index) => index);
    }
    
    // Return indices of documents that have at least one of the selected tags
    return this.uploadedDocuments
      .map((doc, index) => ({ doc, index }))
      .filter(({ doc }) => tags.some(tag => doc.tags.includes(tag)))
      .map(({ index }) => index);
  }
  
  exportAcademicContent(contentType: ContentType, responses: string[]): void {
    if (!responses || responses.length === 0) {
      this.snackBar.open('No content selected for export', 'Close', {
        duration: 3000
      });
      return;
    }
    
    // Get the file format based on content type
    let format;
    if (contentType === 'slides') {
      format = 'pptx';
    } else if (['lecture-notes', 'summary'].includes(contentType)) {
      format = 'docx';
    } else {
      format = 'docx'; // Default format
    }
    
    // Create a fake file for the export function
    const fakeFile = new File([''], `Generated_${contentType}.${format}`, {
      type: format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf'
    });
    
    this.processingStep = `Creating ${this.getContentTypeLabel(contentType)} document...`;
    this.isGenerating = true;
    
    // Use the existing createDownloadableDocument with specialized formatting
    this.aiService.createDownloadableDocument(fakeFile, responses)
      .subscribe(
        blob => {
          // Create a download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Generated_${contentType}_${new Date().toISOString().slice(0, 10)}.${format}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          
          this.isGenerating = false;
          
          this.snackBar.open(`${this.getContentTypeLabel(contentType)} exported successfully`, 'Close', {
            duration: 3000
          });
        },
        error => {
          this.isGenerating = false;
          this.snackBar.open(`Error exporting content: ${error.message}`, 'Close', {
            duration: 5000
          });
        }
      );
  }

  toggleMessageSelection(message: LocalChatMessage): void {
      message.selected = !message.selected;
    
    // Update selected responses
    this.selectedResponses = this.documentChatHistory
      .filter(message => message.selected && message.role === 'assistant')
      .map(message => message.content);
    
    // Show/hide the export button based on selection
    this.showExportButton = this.selectedResponses.length > 0;
  }

  clearDocumentSelection(): void {
    this.documentChatHistory.forEach(message => {
      if (message.selected) {
        message.selected = false;
      }
    });
    this.selectedResponses = [];
    this.showExportButton = false;
  }

  appendToDocument(): void {
    if (!this.selectedDocument) {
      this.snackBar.open('No document selected', 'Close', { duration: 3000 });
      return;
    }

    const selectedMessages = this.documentChatHistory
      .filter(message => message.selected && message.role === 'assistant')
      .map(message => `${message.type?.toUpperCase() || 'RESPONSE'}: ${message.content}`);

    if (selectedMessages.length === 0) {
      this.snackBar.open('No responses selected', 'Close', { duration: 3000 });
      return;
    }

    // Show processing indicator
    this.processingProgress = 30;
    this.processingStep = 'Creating document...';
    this.progressBarMode = 'indeterminate';
    this.isGenerating = true;

    // Log the selected messages for debugging
    console.log(`Processing ${selectedMessages.length} selected messages`);
    console.log(`Document type: ${this.selectedDocument.type}`);

    this.aiService.createDownloadableDocument(this.selectedDocument, selectedMessages)
      .subscribe({
        next: (blob) => {
          try {
            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            
            // Determine file extension based on the blob type
            let fileExtension = this.getFileExtension(this.selectedDocument!.type);
            const actualType = blob.type;
            
            // Check if the format was changed (fallback to HTML)
            if (actualType === 'text/html' && fileExtension !== 'html') {
              fileExtension = 'html';
              this.snackBar.open(
                'Document was converted to HTML format due to encoding issues with the original format', 
                'Close', 
                { duration: 5000 }
              );
            }
            
            const fileName = this.selectedDocument?.name.split('.')[0] + '_with_analysis.' + fileExtension;
            
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            
            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            this.snackBar.open(`Document with analysis downloaded as ${fileExtension.toUpperCase()}`, 'Close', { duration: 3000 });
            
            // Unselect all messages after download
            this.documentChatHistory.forEach(message => message.selected = false);
          } catch (error) {
            console.error('Error downloading document:', error);
            this.snackBar.open('Error downloading document', 'Close', { duration: 3000 });
          } finally {
            // Reset progress
            this.isGenerating = false;
            this.processingProgress = 0;
          }
        },
        error: (error) => {
          console.error('Error creating downloadable document:', error);
          this.snackBar.open(`Error creating document: ${error.message || 'Unknown error'}`, 'Close', { duration: 5000 });
          this.isGenerating = false;
          this.processingProgress = 0;
        }
      });
  }

  // Helper method to get file extension based on MIME type
  private getFileExtension(mimeType: string): string {
    switch (mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      default:
        return 'html';
    }
  }

  resetForm(): void {
    this.textForm.reset();
    if (this.activeInterface === 'document') {
      // Clear only document-related data
      this.selectedDocument = null;
      this.documentContent = null;
      this.uploadedDocuments = [];
      this.selectedDocumentIndices = [];
      this.documentProcessingState = 'idle';
      this.processingProgress = 0;
      this.documentCharacteristics = null;
      
      // Clear document chat history from both local array and service
      this.documentChatHistory = [];
      this.selectedResponses = [];
      this.chatHistoryService.clearDocumentChatHistory();
      
      // Reset the file input element if it exists
      if (this.fileInput && this.fileInput.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      
      // Show notification
      this.snackBar.open('Document analysis reset. Chat history cleared.', 'Close', {
        duration: 3000
      });
    } else if (this.activeInterface === 'academic') {
      // For academic interface, clear the form and history
      this.academicForm.reset({
        contentType: 'lecture-notes',
        academicLevel: 'undergraduate',
        contentLength: 5
      });
      this.focusAreasControl.setValue([]);
      this.selectedFocusAreas = [];
      this.academicChatHistory = [];
      
      // Show notification
      this.snackBar.open('Academic content form reset.', 'Close', {
        duration: 3000
      });
    }
  }

  clearHistory(): void {
    if (this.activeInterface === 'direct') {
      // Clear local array
      this.directChatHistory = [];
      // Clear in service and session storage
      this.chatHistoryService.clearDirectChatHistory();
      
      // Reset the form to ensure a clean state
      this.textForm.reset({
        prompt: ''
      });
      this.chatInputControl.setValue('');
      
      // Focus on the input section after clearing
      setTimeout(() => {
        this.setActiveSection('input-section');
      }, 100);
      
      // Show confirmation
      this.snackBar.open('Chat history cleared', 'Close', {
        duration: 2000
      });
    } else if (this.activeInterface === 'document') {
      // Clear local array
      this.documentChatHistory = [];
      this.selectedResponses = [];
      // Clear in service and session storage
      this.chatHistoryService.clearDocumentChatHistory();
      
      // Reset the form
      this.documentForm.reset({
        question: '',
        documentFocus: 'all',
        customRange: '',
        selectedTags: []
      });
      this.documentChatInputControl.setValue('');
      
      // Focus on the input section after clearing
      setTimeout(() => {
        this.setActiveSection('input-section');
      }, 100);
      
      // Show confirmation
      this.snackBar.open('Document chat history cleared', 'Close', {
        duration: 2000
      });
    }
    
    // Set flag to ensure correct scroll position after view updates
    this.shouldScrollToBottom = false;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (!event.dataTransfer || !event.dataTransfer.files) {
      return;
    }
    
    const files = event.dataTransfer.files;
    
    // Check if there's already a document - only one document allowed
    if (this.uploadedDocuments.length > 0) {
      this.snackBar.open('Only one document is allowed. Please delete the existing document first.', 'Close', {
        duration: 5000
      });
      return;
    }
    
    // Filter for supported file types - but only process the first one
    const file = files[0];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const isValidFile = extension === 'pdf' || extension === 'doc' || extension === 'docx';
    
    if (!isValidFile) {
      this.snackBar.open('Invalid document format. Supported formats: PDF, DOC, DOCX', 'Close', {
        duration: 5000
      });
      return;
    }
    
    // Check file size limit (5MB)
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeInBytes) {
      this.snackBar.open(`File is too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Maximum size is 5MB.`, 'Close', {
        duration: 5000,
        panelClass: 'error-snackbar'
      });
      return;
    }
    
    // Clear document chat history to prevent context from previous documents influencing analysis
    this.documentChatHistory = [];
    this.chatHistoryService.clearDocumentChatHistory();
    this.snackBar.open('Document chat history cleared for new document analysis', 'OK', {
      duration: 3000
    });
    
    // Process only the first file
    this.documentProcessingState = 'uploading';
    
    this.processUploadedDocument(file).then(() => {
      // File processed
      this.documentProcessingState = 'ready';
      
      // Select the document
      this.updateDocumentSelection();
      
      // Show success message
      this.snackBar.open(`Successfully uploaded document: ${file.name}`, 'Close', {
        duration: 3000
      });
    }).catch(error => {
      console.error("Error processing document:", error);
      this.snackBar.open(`Error uploading document: ${error.message}`, 'Close', {
        duration: 5000,
        panelClass: 'error-snackbar'
      });
      this.documentProcessingState = 'error';
    });
  }

  // Helper methods for template
  getSelectedResponsesCount(): number {
    return this.documentChatHistory.filter(m => m.selected).length;
  }

  hasSelectedResponses(): boolean {
    return this.documentChatHistory.some(m => m.selected);
  }
  
  // Method to handle the "Cut" action (remove document but keep chat history)
  cutDocument(): void {
    if (this.documentProcessingState === 'ready') {
      this.selectedDocument = null;
      this.documentContent = null;
      this.documentProcessingState = 'idle';
      this.documentCharacteristics = null;
      
      // Reset AI service document context
      this.aiService.resetDocumentContext();
      
      // Keep the chat history intact
      
      this.snackBar.open('Document removed. Chat history preserved.', 'Close', { 
        duration: 3000 
      });
    }
  }
  
  // Manual scroll to bottom method that can be called from the template
  manualScrollToBottom(): void {
    this.shouldScrollToBottom = true;
    this.isUserScrolling = false;
  }

  // Check if user is at the bottom of the chat
  isAtBottom(): boolean {
    if (!this.chatContainer || !this.chatContainer.nativeElement) {
      return true;
    }
    
    const element = this.chatContainer.nativeElement;
    const atBottom = Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 50;
    return atBottom;
  }

  /**
   * Navigate back to the module selector or previous interface
   */
  goBack(): void {
    // Get the previous navigation state
    const previousItem = this.navigationHistory.getPreviousItem();
    
    if (previousItem && previousItem.module === 'text' && previousItem.interfaceType 
        && previousItem.interfaceType !== this.activeInterface) {
      // If previous state was a different interface within text module, switch to that
      this.switchInterface(previousItem.interfaceType as 'direct' | 'document' | 'academic');
      
      // Remove the current navigation item to avoid loops
      this.navigationHistory.goBack();
      
      // Show confirmation
      this.snackBar.open(`Returned to ${this.getInterfaceLabel(previousItem.interfaceType as 'direct' | 'document' | 'academic')}`, 'Close', {
        duration: 2000
      });
    } else {
      // Otherwise go back to module selector
      this.router.navigate(['/']);
    }
  }

  // For backward compatibility with any existing code
  async handleFileSelection(file: File): Promise<void> {
    await this.processUploadedDocument(file);
  }

  // Helper to check if a response appears to be truncated
  private responseAppearsTruncated(text: string): boolean {
    // Check for common truncation patterns
    const truncationIndicators = [
      /[^.!?]\s*$/,  // Doesn't end with punctuation
      /\.\.\.\s*$/,  // Ends with ellipsis
      /To be continued/i,
      /I'll continue/i,
      /As mentioned earlier.*$/,
      /In conclusion.*$/  // Conclusion section started but cut off
    ];
    
    return truncationIndicators.some(pattern => pattern.test(text.trim()));
  }

  private getInitialGuidance(formValues: any): string {
    // Skip initial guidance if content is already specified
    if (formValues.topic && formValues.contentLength) {
      const contentType = this.getContentTypeLabel(formValues.contentType).toLowerCase();
      const contentUnit = this.getContentUnit(formValues.contentType);
      return `Creating ${formValues.contentLength} ${contentUnit} on "${formValues.topic}" for ${formValues.academicLevel} level...`;
    }
    return '';
  }

  /**
   * Get appropriate unit name for the content type
   */
  private getContentUnit(contentType: string): string {
    switch (contentType) {
      case 'slides':
        return 'Slides';
      case 'lecture-notes':
        return 'Sections';
      case 'exam':
      case 'quiz':
        return 'Questions';
      case 'exercises':
        return 'Exercises';
      case 'summary':
        return 'Paragraphs';
      default:
        return 'Content';
    }
  }

  private getContentTypeInstructions(contentType: string): string {
    switch (contentType) {
      case 'slides':
        return 'Create a professional presentation with clear slide titles, bullet points, and visual organization. Each slide should focus on a specific aspect. Include an introduction slide and a conclusion slide. ';
      case 'lecture-notes':
        return 'Create detailed lecture notes with clear sections, key concepts, examples, and explanations. Include definitions, diagrams descriptions, and practical applications where relevant. ';
      case 'exercises':
        return 'Create practical exercises that test understanding and application. Include a mix of conceptual and hands-on exercises. Each exercise should be clearly structured with context and requirements. ';
      case 'quiz':
        return 'Create focused quiz questions that test understanding of key concepts. Include a mix of question types (multiple choice, short answer, etc.). Each question should be clear and specific. ';
      case 'exam':
        return 'Create comprehensive exam questions that test deep understanding. Include a mix of theoretical and practical questions. Questions should require critical thinking and application of concepts. ';
      default:
        return 'Create well-structured content with clear organization and comprehensive coverage. ';
    }
  }

  private getDocumentContext(documentNames: string[]): string {
    let context = '';
    for (const index of this.selectedDocumentIndices) {
      const doc = this.uploadedDocuments[index];
      documentNames.push(doc.file.name);
      context += `\n\n--- DOCUMENT: ${doc.file.name} ---\n\n${doc.content}`;
    }
    return context;
  }

  // Update the validation method to make focus areas optional but with a maximum limit
  isFocusAreaCountValid(): boolean {
    const selectedAreas = this.focusAreasControl.value as string[];
    const contentLength = this.academicForm.get('contentLength')?.value || 1;
    
    // Focus areas are optional, but cannot exceed content length
    if (selectedAreas.length > contentLength) {
      this.snackBar.open(
        `Maximum Focus Areas Exceeded: You can select at most ${contentLength} focus area${contentLength > 1 ? 's' : ''} for ${contentLength} ${this.getContentUnit(this.academicForm.get('contentType')?.value)}.`, 
        'OK', 
        {
          duration: 5000,
          panelClass: ['modern-snackbar', 'warning-snackbar'],
          verticalPosition: 'top',
          horizontalPosition: 'center'
        }
      );
      return false;
    }
    
    return true;
  }

  /**
   * Handle focus area changes with a simplified approach
   */
  onFocusAreaChange(event: any): void {
    this.selectedFocusAreas = event.value;
  }

  /**
   * Remove a focus area
   */
  removeFocusArea(area: string): void {
    const currentAreas = this.focusAreasControl.value as string[];
    this.focusAreasControl.setValue(currentAreas.filter(a => a !== area));
    this.selectedFocusAreas = this.selectedFocusAreas.filter(a => a !== area);
  }

  // Opens the document analysis guide in a simple dialog
  openDocumentAnalysisGuide(): void {
    // Fetch the guide content directly
    this.http.get('assets/documents/document-analysis-guide.md', { responseType: 'text' })
      .pipe(
        catchError(error => {
          console.error('Error loading guide:', error);
          this.snackBar.open('Could not load the document analysis guide', 'Dismiss', {
            duration: 3000,
            panelClass: ['error-snackbar', 'modern-snackbar']
          });
          return of('# Document Analysis Guide\n\nFailed to load content.');
        })
      )
      .subscribe(content => {
        // Convert markdown to simple HTML
        const htmlContent = this.convertMarkdownToHtml(content);
        
        // Open a dialog with the content
        this.dialog.open(GuideDialogComponent, {
          width: '800px',
          data: {
            title: 'Document Analysis Guide',
            content: htmlContent
          },
          panelClass: 'guide-dialog'
        });
      });
  }
  
  // Simple markdown to HTML converter for basic formatting
  private convertMarkdownToHtml(markdown: string): string {
    if (!markdown) return '';
    
    const text = markdown as string;
    
    // Simple markdown to HTML conversion
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      
      // Emphasis
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      
      // Links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
      
      // Line breaks
      .replace(/\n/g, '<br>');
    
    return html;
  }

  // Add a special handler for academic content clicking
  @HostListener('click', ['$event'])
  onContentClick(event: MouseEvent): void {
    // If clicking on academic content, prevent auto-scrolling for a longer period
    if (this.activeInterface === 'academic' && 
        (event.target as HTMLElement).closest('.academic-content, .formatted-content, .message-content, .initial-guidance')) {
      
      // Set user scrolling flag to prevent auto-scrolling
      this.isUserScrolling = true;
      
      // Clear any existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // Keep the scrolling disabled for a long time
      this.scrollTimeout = setTimeout(() => {
        // Only re-enable auto-scrolling if at the bottom
        if (this.chatContainer && this.chatContainer.nativeElement) {
          const element = this.chatContainer.nativeElement;
          const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;
          
          if (isAtBottom) {
            this.isUserScrolling = false;
          }
        }
      }, 30000); // 30 seconds
    }
  }

  /**
   * Send a message from the chat input
   */
  sendMessage(content?: string): void {
    if (this.isGenerating) {
      return;
    }

    // Use either provided content or the chat input value
    const messageContent = content || this.chatInputControl.value?.trim();
    if (!messageContent) {
      return;
    }

    // Check if this is the first message
    const isFirstMessage = this.directChatHistory.length === 0;

    // Add user message to chat history
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
      id: this.generateMessageId()
    };
    this.chatHistoryService.addDirectChatMessage(userMessage);
    
    // Update local direct chat history
    this.directChatHistory = this.chatHistoryService.getDirectChatHistory();

    // Clear input fields - clear both the form control and the chat input
    if (!content) {
      this.chatInputControl.setValue('');
      if (this.textForm.get('prompt')) {
        this.textForm.get('prompt')?.setValue('');
      }
    }

    // Scroll to bottom and focus on output section
    this.shouldScrollToBottom = true;
    if (isFirstMessage) {
      setTimeout(() => {
        this.setActiveSection('output-section');
        // Focus on the right input field
        const rightInputField = document.querySelector('.chat-input textarea') as HTMLTextAreaElement;
        if (rightInputField) {
          rightInputField.focus();
        }
      }, 500);
    }

    // Generate AI response
    this.generateDirectResponse();
  }
  
  /**
   * Generate a direct response from the AI based on chat history
   */
  private async generateDirectResponse(): Promise<void> {
    try {
      // Set generating state
      this.isGenerating = true;
      this.processingProgress = 20;
      this.processingStep = 'Thinking...';
      
      // Prepare conversation history for the AI - get ALL messages from history
      const conversationHistory = this.directChatHistory
        .slice(-8) // Limit to last 8 messages to avoid token limits
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));
      
      // No need to add the user message - it's already in the history
      // The last message in history is the user's most recent message
      
      // Send to AI service
      this.processingProgress = 50;
      this.processingStep = 'Generating response...';
      
      // Use the generateText method with message array 
      const response = await this.aiService.generateText(conversationHistory).toPromise();
      
      this.processingProgress = 90;
      this.processingStep = 'Finishing up...';
      
      // Add AI response to chat history via service
      this.chatHistoryService.addDirectChatMessage({
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        id: this.generateMessageId(),
        type: 'text'
      });
      
      // Set flag to scroll to bottom
      this.shouldScrollToBottom = true;
      
      // Clear the input field - only clear prompt if we're using the form
      this.chatInputControl.setValue('');
      
    } catch (error: any) {
      console.error('Error generating text response:', error);
      
      let errorMessage = "I'm sorry, I couldn't generate a response.";
      
      if (error && error.message) {
        if (error.message.includes('API')) {
          errorMessage = "I'm sorry, there was an API error. Please try again later.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "I'm sorry, the request timed out. Please try a shorter prompt or try again later.";
        }
      }
      
      // Add error message to chat history
      this.chatHistoryService.addDirectChatMessage({
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        id: this.generateMessageId(),
        type: 'text'
      });
      
      // Set flag to scroll to bottom
      this.shouldScrollToBottom = true;
      
    } finally {
      // Reset the processing state
      this.isGenerating = false;
      this.processingProgress = 0;
      this.processingStep = '';
    }
  }

  /**
   * Format the response text for better display
   */
  private formatResponseForDisplay(text: string): string {
    // Let the AIService formatResponseContent handle most of the formatting
    // This will maintain consistency between the component and service
    if (this.aiService && typeof this.aiService.getFormattedContent === 'function') {
      return this.aiService.getFormattedContent(text);
    }
    
    // If AIService method isn't available, fall back to simple formatting
    let formatted = text;
    
    // Ensure line breaks are preserved
    formatted = formatted.replace(/\n/g, '<br>');
    
    // Make simple markdown elements more readable
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // Bold
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic
    
    // Format lists
    formatted = formatted.replace(/^- (.*?)$/gm, '• $1');
    
    // Ensure proper spacing
    formatted = formatted.replace(/<br><br><br>/g, '<br><br>');
    
    // Sanitize the HTML to prevent XSS attacks
    return formatted;
  }

  /**
   * Handle Enter key press in the chat input
   * @param event The keyboard event
   */
  handleEnterKey(event: KeyboardEvent): void {
    // If Shift+Enter is pressed, allow default behavior (new line)
    if (event.shiftKey) {
      return;
    }
    
    // If Enter without Shift is pressed, send message and prevent default
    event.preventDefault();
    this.sendMessage();
  }

  /**
   * Handle Enter key press in the document chat input
   * @param event The keyboard event
   */
  handleDocumentEnterKey(event: KeyboardEvent): void {
    // If Shift+Enter is pressed, allow default behavior (new line)
    if (event.shiftKey) {
      return;
    }
    
    // If Enter without Shift is pressed, send message and prevent default
    event.preventDefault();
    this.sendDocumentMessage();
  }

  /**
   * Handle document queries entered by the user
   */
  sendDocumentMessage(): void {
    if (this.isGenerating || !this.documentChatInputControl.value) return;
    
    const userInput = this.documentChatInputControl.value.trim();
    if (!userInput) return;
    
    // Check if this is the first message
    const isFirstMessage = this.documentChatHistory.length === 0;
    
    // Set the question in the form
    this.documentForm.get('question')?.setValue(userInput);
    
    // Check if this is a normal message that happens to contain quick action keywords
    // This ensures we don't confuse regular queries with quick actions
    this.ensureNoQuickActionConfusion(userInput);
    
    // Add the message to the chat history
    this.chatHistoryService.addDocumentChatMessage({
      role: 'user',
      content: userInput,
      timestamp: new Date(),
      id: this.generateMessageId()
    });
    
    // Clear the input field
    this.documentChatInputControl.setValue('');
    
    // Set document processing state
    this.documentProcessingState = 'analyzing';
    
    // If this is the first message, focus on the output section
    if (isFirstMessage) {
      setTimeout(() => {
        this.setActiveSection('output-section');
        // Focus on the right input field
        const rightInputField = document.querySelector('.chat-input textarea') as HTMLTextAreaElement;
        if (rightInputField) {
          rightInputField.focus();
        }
      }, 500);
    }
    
    // Generate content
    this.generateContent();
  }
  
  /**
   * Ensures that a user-entered query containing keywords like "summary" or "key points"
   * is not confused with the quick action buttons
   */
  private ensureNoQuickActionConfusion(userInput: string): void {
    // List of common keywords that might be in both user queries and quick actions
    const keywordPatterns = [
      /key\s*points/i,
      /bullet\s*points/i,
      /summary/i,
      /summarize/i,
      /outline/i,
      /presentation/i,
      /slides/i
    ];
    
    // Check if the input contains a quick action marker - if so, it's an actual quick action
    if (userInput.includes('[QUICK_ACTION:')) {
      return; // This is a legitimate quick action, not confusion
    }
    
    // Check if any patterns match but this is a user-entered query (not from quick action buttons)
    const containsKeywords = keywordPatterns.some(pattern => pattern.test(userInput));
    
    if (containsKeywords) {
      // Clear any _currentSuggestion to ensure it's treated as a regular query
      this._currentSuggestion = null;
    }
  }

  /**
   * Handle Enter key press in the academic chat input
   * @param event The keyboard event
   */
  handleAcademicEnterKey(event: KeyboardEvent): void {
    // If Shift+Enter is pressed, allow default behavior (new line)
    if (event.shiftKey) {
      return;
    }
    
    // If Enter without Shift is pressed, send message and prevent default
    event.preventDefault();
    this.sendAcademicMessage();
  }

  /**
   * Send a message from the academic chat input
   */
  sendAcademicMessage(): void {
    const message = this.academicChatInputControl.value?.trim();
    if (!message) return;
    
    // Add user message to chat history
    this.academicChatHistory.push({
      role: 'user',
      content: message,
      timestamp: new Date(),
      id: this.generateMessageId() // Add ID
    });
    
    // Clear the input
    this.academicChatInputControl.setValue('');
    
    // Scroll to bottom
    this.shouldScrollToBottom = true;
    this.scrollToBottom();
    
    // Only generate response if not already generating
    if (!this.isGenerating) {
      // Add a small delay to ensure the UI updates first
      setTimeout(() => {
        this.handleAcademicContent(message);
      }, 100);
    }
  }

  /**
   * Analyze a specific chunk of the document to handle large documents
   */
  analyzeDocumentChunk(): void {
    if (!this.uploadedDocuments.length) {
      this.snackBar.open('No document uploaded', 'Close', { duration: 3000 });
      return;
    }
    
    const doc = this.uploadedDocuments[0];
    
    // Add a dialog to let the user specify which chunk to analyze
    this.dialog.open(ChunkSelectionDialogComponent, {
      width: '500px',
      data: {
        fileName: doc.file.name,
        contentLength: doc.content.length,
        // Estimate approximate page count based on average characters per page
        estimatedPages: Math.ceil(doc.content.length / 3000),
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        // If the user selected a chunk, update the focusRange
        const { startPage, endPage } = result;
        
        if (startPage && endPage) {
          // Convert pages to character ranges (rough approximation)
          const charsPerPage = 3000; // Approximate chars per page
          const start = (startPage - 1) * charsPerPage;
          const end = Math.min(endPage * charsPerPage, doc.content.length);
          
          // Extract the content chunk
          const chunkContent = doc.content.substring(start, end);
          
          // Create a new document object for this chunk
          const chunkDoc: UploadedDocument = {
            ...doc,
            content: doc.content, // Keep full content for reference
            focusRange: { start: startPage - 1, end: endPage - 1 }
          };
          
          // Replace the current document with the chunk
          this.uploadedDocuments[0] = chunkDoc;
          
          // Update the AI service with the processed chunk
          this.aiService.updateDocumentContext(chunkContent);
          
          // Notify the user
          this.snackBar.open(`Now analyzing pages ${startPage}-${endPage} of document`, 'Close', {
            duration: 5000
          });
        }
      }
    });
  }

  // Message editing methods
  startEditingMessage(message: ChatMessage): void {
    if (message.role === 'user') {
      this.editingMessageId = message.id!;
      this.editMessageContent = message.content;
    }
  }

  cancelEditing(): void {
    this.editingMessageId = null;
    this.editMessageContent = '';
  }

  saveEditedMessage(): void {
    if (this.editingMessageId) {
      // Store the edited message content before canceling edit mode
      const editedContent = this.editMessageContent;
      
      // Update the message content in the chat history
      if (this.activeInterface === 'direct') {
        this.chatHistoryService.editDirectChatMessage(
          this.editingMessageId,
          editedContent
        );
        
        // Reset editing state
        this.cancelEditing();
        
        // For direct chat, use the existing generateDirectResponse
        this.generateDirectResponse();
      } else {
        // For document chat, handle specially to ensure proper message flow
        // Find the message's position in the history to maintain conversation context
        const currentHistory = this.documentChatHistory;
        const messageIndex = currentHistory.findIndex(m => m.id === this.editingMessageId);
        
        if (messageIndex >= 0) {
          // Update the message in history first
          this.chatHistoryService.editDocumentChatMessage(
            this.editingMessageId,
            editedContent
          );
          
          // Get the updated history
          this.documentChatHistory = this.chatHistoryService.getDocumentChatHistory();
          
          // Reset editing state
          this.cancelEditing();
          
          // Show processing feedback
          this.isGenerating = true;
          this.documentProcessingState = 'analyzing';
          this.processingStep = 'Processing your request';
          
          // Get all messages up to the edited message for context
          const previousMessages = this.documentChatHistory
            .slice(0, messageIndex)
            .map(msg => ({
              role: msg.role,
              content: msg.content
            }));
          
          // Process the document query with the edited message
          this.handleDocumentQuery(editedContent, undefined, true)
            .then(() => {
              // After processing, remove all messages after the edited message
              const updatedHistory = this.documentChatHistory.slice(0, messageIndex + 2);
              this.chatHistoryService.setDocumentChatHistory(updatedHistory);
              this.documentChatHistory = updatedHistory;
              
              // Scroll to the new response
              this.shouldScrollToBottom = true;
              this.scrollToBottom();
            })
            .catch(error => {
              console.error('Error processing edited message:', error);
              this.snackBar.open('Error processing your edited message', 'Close', {
                duration: 3000
              });
            });
        } else {
          // If message not found, just show confirmation and reset editing state
          this.snackBar.open('Message updated successfully', 'Close', {
            duration: 2000
          });
          this.cancelEditing();
        }
      }
    }
  }

  /**
   * Sets a suggestion text as the document question and focuses the textarea
   * @param suggestion The text suggestion to set
   * @param autoGenerate If true, automatically starts the generation process
   * @param actionType Optional identifier for the type of quick action
   */
  selectSuggestion(suggestion: string, autoGenerate: boolean = false, actionType?: string): void {
    // Clear any previous suggestion data
    this._currentSuggestion = null;
    
    // Generate a unique ID for this specific action request
    const actionId = this.generateMessageId().substring(4, 12);
    
    // Create enhanced suggestion with quick action marker, unique ID, and instruction to create distinct result
    const enhancedSuggestion = actionType 
      ? `[QUICK_ACTION:${actionType}:${actionId}] ${suggestion}` 
      : suggestion;
    
    // Store suggestion with its metadata
    this._currentSuggestion = {
      original: suggestion,
      enhanced: enhancedSuggestion,
      actionType: actionType,
      actionId: actionId
    };
    
    // Update the appropriate form based on active interface
    if (this.activeInterface === 'document' && this.documentForm) {
      this.documentForm.get('question')?.setValue(suggestion);
    } else if (this.activeInterface === 'direct' && this.textForm) {
      this.textForm.get('prompt')?.setValue(suggestion);
    }
    
    // Generate content if autoGenerate is true
    if (autoGenerate) {
      this.generateContent(true);
    }
  }

  // Opens the general query guide in a dialog
  openGeneralQueryGuide(): void {
    // Fetch the guide content directly
    this.http.get('assets/documents/general-query-guide.md', { responseType: 'text' })
      .pipe(
        catchError(error => {
          console.error('Error loading guide:', error);
          this.snackBar.open('Could not load the general query guide', 'Dismiss', {
            duration: 3000,
            panelClass: ['error-snackbar', 'modern-snackbar']
          });
          return of('# General Query Guide\n\nFailed to load content.');
        })
      )
      .subscribe(content => {
        // Convert markdown to simple HTML
        const htmlContent = this.convertMarkdownToHtml(content);
        
        // Open a dialog with the content
        this.dialog.open(GuideDialogComponent, {
          width: '800px',
          data: {
            title: 'General Query Guide',
            content: htmlContent
          },
          panelClass: 'guide-dialog'
        });
      });
  }

  // Opens the main text module guide explaining how everything works
  openTextModuleGuide(): void {
    // Fetch the guide content directly
    this.http.get('assets/documents/text-module-guide.md', { responseType: 'text' })
      .pipe(
        catchError(error => {
          console.error('Error loading guide:', error);
          this.snackBar.open('Could not load the AI Assistant guide', 'Dismiss', {
            duration: 3000,
            panelClass: ['error-snackbar', 'modern-snackbar']
          });
          return of('# AI Assistant Guide\n\nFailed to load content.');
        })
      )
      .subscribe(content => {
        // Convert markdown to simple HTML
        const htmlContent = this.convertMarkdownToHtml(content);
        
        // Open a dialog with the content
        this.dialog.open(GuideDialogComponent, {
          width: '800px',
          data: {
            title: 'AI Assistant Guide',
            content: htmlContent
          },
          panelClass: 'guide-dialog'
        });
      });
  }

  /**
   * Toggles between light and dark modes
   */
  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    
    // Save preference to localStorage
    localStorage.setItem('ai-assistant-dark-mode', this.isDarkMode ? 'true' : 'false');
    
    // Apply theme class to document root element for CSS variables
    if (this.isDarkMode) {
      document.documentElement.classList.add('theme-dark');
      document.body.classList.add('theme-alternate');
    } else {
      document.documentElement.classList.remove('theme-dark');
      document.body.classList.remove('theme-alternate');
    }
    
    // Show snackbar confirmation
    this.snackBar.open(`${this.isDarkMode ? 'Dark' : 'Light'} mode activated`, 'Close', {
      duration: 2000,
      panelClass: ['modern-snackbar']
    });
  }

  /**
   * Set the active section with visual highlight
   * @param id The ID of the active section
   */
  setActiveSection(id: string): void {
    this.activeSectionId = id;
    
    // Remove active class from all cards
    const cards = document.querySelectorAll('.input-card, .chat-history-card');
    cards.forEach(card => {
      card.classList.remove('active-card');
    });
    
    // Add active class to the current card
    const activeCard = document.getElementById(id);
    if (activeCard) {
      activeCard.classList.add('active-card');
    }
  }

  /**
   * Convert action type code to user-friendly label
   */
  getActionTypeLabel(actionType: string): string {
    switch (actionType) {
      case 'KEY_POINTS':
        return 'Key Points';
      case 'SUMMARY':
        return 'Summary';
      case 'PRESENTATION':
        return 'Presentation Ideas';
      case 'OUTLINE':
        return 'Document Outline';
      default:
        return actionType;
    }
  }

  /**
   * Extracts table formatting instructions from a prompt
   * @param prompt User's input prompt
   * @returns A string with formatting instructions or undefined if none found
   */
  private extractTableFormatInstructions(prompt: string): string | undefined {
    // Check if the prompt contains table-related keywords
    const hasTableRequest = /table|comparison|grid|matrix|chart/i.test(prompt);
    
    if (hasTableRequest) {
      return `Format Instructions:
- Format as a properly structured table with clear headers and aligned columns. 
- Use ONLY markdown table format.
- Make sure to align columns properly for better readability.
- Create a well-formatted table using markdown syntax with proper column headers and aligned rows.
- Structure the table logically to effectively compare the requested elements.
- IMPORTANT: Use ONLY markdown format for tables. Do not duplicate tables in different formats.`;
    }
    
    return undefined;
  }

  // Creates a message ID
  private generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
} // End of TextModuleComponent